<template>
  <div
    class="relative flex min-h-0 grow flex-col overflow-hidden"
    data-target="page-content"
  >
    <template v-if="source.isLoading">
      <TableSkeleton
        :table="table"
        :hide-header="hideHeader"
        :row-count="placeholderRowCount"
      />
    </template>
    <template v-else-if="source.error && !source.isFetchNextPageError">
      <EmptyStateContainer>
        <EmptyState icon="no-symbol" title="Error">
          <p>{{ mungeError(source.error) }}</p>

          <div class="mt-4">
            <ActionButton
              text="Retry"
              icon="arrow-path"
              @click="source.refetch"
            />
          </div>
        </EmptyState>
      </EmptyStateContainer>
    </template>
    <template v-else-if="slots.emptyState && table.isEmpty">
      <EmptyStateContainer>
        <slot name="emptyState"></slot>
      </EmptyStateContainer>
    </template>
    <template v-else>
      <Table :table="table" :hide-header="hideHeader">
        <template v-if="slots.dragPreview" #dragPreview="{ item }">
          <slot name="dragPreview" :item="item" />
        </template>

        <template #footer>
          <div
            v-if="source.isRefetching"
            class="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-gray-600 px-2 py-1 text-sm text-white"
          >
            <ArrowPathIcon class="mx-auto size-4 animate-spin" />
            <span>Loading</span>
          </div>

          <div v-if="source.hasNextPage" class="m-4 mb-6 shrink-0">
            <ActionButton
              text="Load more"
              :disabled="source.isFetchingNextPage"
              :pending="source.isFetchingNextPage"
              @click="source.fetchNextPage"
            />
          </div>
        </template>
      </Table>

      <SelectionWidget
        v-if="selectWidget && selection"
        v-model="selection.state.value"
        :definition="selectWidget"
        :items="source.data"
        :total-count="source.count"
        @can-select="selection.canSelect"
      />
    </template>
  </div>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import ActionButton from "@/design_system/Utilities/ActionButton.vue";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-outline.svg";
import type { TableInstance } from "@/lib/tanstack-table";
import type { GenericModel } from "@/types/model";

import EmptyState from "./EmptyState.vue";
import EmptyStateContainer from "./EmptyStateContainer.vue";
import SelectionWidget from "./internal/SelectionWidget.vue";
import Table from "./internal/Table.vue";
import TableSkeleton from "./internal/TableSkeleton.vue";

const props = defineProps<{
  table: TableInstance<Model>;
  hideHeader?: boolean;
  placeholderRowCount?: number;
}>();

const slots = defineSlots<{
  dragPreview?(props: { item: Model }): unknown;
  emptyState?(props: {}): unknown;
}>();

const table = props.table.internal;
const source = table.source;

const selection = table.instance.options.meta?.selection;
const selectWidget = selection?.widget;

const mungeError = (error: unknown) => {
  if ((error as any).data?.detail?.[0]?.msg) {
    return (error as any).data?.detail[0]?.msg;
  }

  if ((error as any).data?.detail?.[0]?.value) {
    return (error as any).data?.detail[0]?.value;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return error;
};
</script>
